import { ThemeEvents } from '@theme/events';
import { checkFaceValueLimit } from '@orchard/gift-card-limits';

/**
 * Orchard's product form behavior extensions.
 * Loaded as a module on product pages to add:
 * 1. Care protection: after ATC, add protection product with matching token, then redirect to cart
 * 2. Gift card face value limit enforcement
 * 3. Always-redirect-to-cart behavior (Orchard doesn't use cart drawer on PDP)
 */

let isHandlingProtection = false;

/**
 * After a successful cart add, check for care protection and chain the protection add.
 * Then redirect to cart.
 */
document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  // Only handle events from product-form-component
  if (event.detail?.data?.source !== 'product-form-component') return;
  // Skip error events
  if (event.detail?.data?.didError) return;
  // Prevent re-entrancy from the protection add triggering another cart:update
  if (isHandlingProtection) return;

  const careProtection = /** @type {any} */ (document.querySelector('care-protection-component'));

  if (careProtection?.isChecked) {
    isHandlingProtection = true;

    const productTitle =
      document.querySelector('.product-title')?.textContent?.trim() ||
      document.querySelector('h1')?.textContent?.trim() ||
      '';

    try {
      await careProtection.addProtectionToCart(productTitle);
    } catch (err) {
      console.error('Failed to add care protection:', err);
    } finally {
      isHandlingProtection = false;
    }
  }

  // Orchard always redirects to cart after adding a product
  window.location.href = Theme.routes.cart_url;
});

/**
 * Gift card face value limit check.
 * Intercepts form submission for limited gift card products.
 */
document.addEventListener(
  'submit',
  async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    const productForm = form?.closest?.('product-form-component');
    if (!productForm) return;

    const limitedGiftCard = productForm.closest('[data-limited-gift-card="true"]');
    if (!limitedGiftCard) return;

    const faceValue = parseInt(/** @type {HTMLElement} */ (limitedGiftCard).dataset.giftCardFaceValue || '0', 10);
    if (faceValue <= 0) return;

    // Prevent the default submit so we can validate
    event.preventDefault();
    event.stopImmediatePropagation();

    const quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    const quantity = parseInt(quantityInput?.value || '1', 10);

    const { allowed } = await checkFaceValueLimit(faceValue, quantity);
    if (!allowed) {
      const errorEl = productForm.querySelector('[ref="addToCartTextError"]');
      if (errorEl) {
        errorEl.classList.remove('hidden');
        const textNode = errorEl.childNodes[2];
        const msg = 'Gift card face value limit of $100 reached for this order.';
        if (textNode) {
          textNode.textContent = msg;
        } else {
          errorEl.appendChild(document.createTextNode(msg));
        }
        setTimeout(() => errorEl.classList.add('hidden'), 5000);
      }
      return;
    }

    // Validation passed — re-submit without our listener catching it again
    form.requestSubmit();
  },
  { capture: true }
);

/**
 * L3 — authoritative submit-time variant guard.
 *
 * The submitted SKU comes from product-form's #getIntendedVariantId(): the URL
 * `?variant=` first, then the hidden input[name="id"]. Both are maintained
 * reactively and can lag the visible selection (abort races, forced/keyboard
 * submits that slip past the disabled button). Here we recompute the variant
 * from the picker AT submit time and either block (partial/unavailable) or pin
 * BOTH id sources to the recomputed value — so a stale-but-valid id can never be
 * submitted as the wrong SKU. Capture phase, so it runs before product-form's
 * own handler reads those sources. Products without a local catalog (cards,
 * non-phone) are left to the server.
 */
document.addEventListener(
  'submit',
  (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    const productForm = form?.closest?.('product-form-component');
    if (!productForm) return;

    const picker = /** @type {any} */ (document.querySelector('variant-picker[data-template-product-match="true"]'));
    if (!picker || typeof picker.getSubmitVariantId !== 'function') return;

    const resolved = picker.getSubmitVariantId();
    if (resolved === undefined) return; // no local catalog → server resolves; don't interfere

    if (resolved === null) {
      // Partial/unavailable selection reached submit (race, forced submit). Block
      // and restore the correct, locale-aware prompt + disabled state.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (typeof picker.syncAddToCartState === 'function') picker.syncAddToCartState();
      return;
    }

    // Resolved to an available variant — pin both id sources the form will read.
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') !== resolved) {
      url.searchParams.set('variant', resolved);
      history.replaceState({}, '', url.toString());
    }
    const hidden = /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="id"]'));
    if (hidden && hidden.value !== resolved) hidden.value = resolved;
  },
  { capture: true }
);
